import {
	Badge,
	Box,
	Button,
	Flex,
	Icon,
	Input,
	Progress,
	Spinner,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
	Textarea,
	useColorModeValue
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { format } from 'date-fns';
import { MdCloudUpload, MdContentPaste, MdDownload, MdInsertDriveFile } from 'react-icons/md';
import Card from '../../components/card/Card';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import { createBatchFromCpfs, fetchBatches, uploadBatchFile } from '../../store/features/batch/batch.slice';
import { BatchStatus } from '../../models/batch.model';
import { datricApi } from '../../services/api/datric.api';
import { toastService } from '../../services/toast.service';

const STATUS_META: Record<BatchStatus, { label: string; scheme: string }> = {
	[BatchStatus.PENDING]: { label: 'Pendente', scheme: 'gray' },
	[BatchStatus.PROCESSING]: { label: 'Processando', scheme: 'blue' },
	[BatchStatus.COMPLETED]: { label: 'Concluído', scheme: 'green' },
	[BatchStatus.FAILED]: { label: 'Falhou', scheme: 'red' }
};

// Aceita CPFs separados por quebra de linha, vírgula, ponto-e-vírgula, tab ou
// espaço — com ou sem máscara. Dedup preservando a ordem de colagem.
// CPFs com menos de 11 dígitos (zeros à esquerda perdidos ao copiar de planilha)
// são completados com zeros à frente. A validação de dígito verificador fica no
// servidor (normalizeCpfList).
function extractCpfs(text: string): string[] {
	const seen = new Set<string>();
	for (const token of text.split(/[\s,;|]+/)) {
		let digits = token.replace(/\D/g, '');
		if (digits.length > 0 && digits.length < 11) digits = digits.padStart(11, '0');
		if (digits.length === 11) seen.add(digits);
	}
	return Array.from(seen);
}

export default function Lotes() {
	const dispatch = useAppDispatch();
	const { list, loading, uploading } = useAppSelector((state: RootState) => state.batch);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
	const dropBg = useColorModeValue('secondaryGray.300', 'navy.900');

	const [file, setFile] = useState<File | null>(null);
	const [name, setName] = useState('');
	const [tabIndex, setTabIndex] = useState(0);
	const [pastedText, setPastedText] = useState('');

	const pastedCpfs = useMemo(() => extractCpfs(pastedText), [pastedText]);

	useEffect(() => {
		dispatch(fetchBatches());
		const interval = setInterval(() => dispatch(fetchBatches()), 10000);
		return () => clearInterval(interval);
	}, [dispatch]);

	// Ctrl+V em qualquer lugar da tela (fora de um campo de texto) joga a lista
	// colada direto na aba "Colar lista".
	useEffect(() => {
		const onPaste = (event: ClipboardEvent) => {
			const target = event.target as HTMLElement | null;
			const tag = target?.tagName;
			if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;

			const text = event.clipboardData?.getData('text') ?? '';
			if (extractCpfs(text).length === 0) return;

			event.preventDefault();
			setTabIndex(1);
			setPastedText(prev => (prev ? `${prev}\n${text}` : text));
		};
		window.addEventListener('paste', onPaste);
		return () => window.removeEventListener('paste', onPaste);
	}, []);

	const onDrop = useCallback((accepted: File[]) => {
		if (accepted.length > 0) {
			setFile(accepted[0]);
			if (!name) setName(accepted[0].name.replace(/\.[^.]+$/, ''));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		// react-dropzone v12: `accept` é string de MIME/extensões (o formato objeto só existe a partir da v14).
		accept: '.csv, .xls, .xlsx, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		maxFiles: 1
	});

	const handleUpload = () => {
		if (tabIndex === 1) {
			if (pastedCpfs.length === 0) {
				toastService.warning('Cole ao menos um CPF válido');
				return;
			}
			dispatch(
				createBatchFromCpfs({
					name: name || `Lote colado ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
					cpfs: pastedCpfs
				})
			);
			setPastedText('');
			setName('');
			return;
		}

		if (!file) {
			toastService.warning('Selecione um arquivo CSV ou XLSX');
			return;
		}
		dispatch(uploadBatchFile({ name: name || file.name, file }));
		setFile(null);
		setName('');
	};

	const handleDownload = async (id: string) => {
		try {
			await datricApi.batch.download(id);
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao baixar arquivo');
		}
	};

	return (
		<Box pt='10px'>
			<Card mb='20px'>
				<Text fontWeight='800' fontSize='lg' color={textColor} mb='4px'>
					Enviar lote
				</Text>
				<Text color={subColor} fontSize='sm' mb='16px'>
					Envie um arquivo CSV/XLSX com uma coluna de CPFs, ou cole a lista direto (Ctrl+V).
				</Text>

				<Tabs index={tabIndex} onChange={setTabIndex} variant='soft-rounded' colorScheme='blue'>
					<TabList mb='16px'>
						<Tab fontSize='sm'>
							<Icon as={MdCloudUpload} mr='6px' /> Arquivo
						</Tab>
						<Tab fontSize='sm'>
							<Icon as={MdContentPaste} mr='6px' /> Colar lista
						</Tab>
					</TabList>

					<TabPanels>
						<TabPanel p='0'>
							<Box
								{...getRootProps()}
								border='2px dashed'
								borderColor={isDragActive ? 'brand.500' : 'secondaryGray.400'}
								bg={dropBg}
								borderRadius='16px'
								p='40px'
								textAlign='center'
								cursor='pointer'
								transition='.2s all ease'>
								<input {...getInputProps()} />
								<Icon as={MdCloudUpload} w='48px' h='48px' color='brand.500' mb='10px' />
								<Text color={textColor} fontWeight='600'>
									{file
										? file.name
										: isDragActive
										? 'Solte o arquivo aqui...'
										: 'Arraste um arquivo ou clique para selecionar'}
								</Text>
								<Text color={subColor} fontSize='xs' mt='6px'>
									CSV, XLS ou XLSX
								</Text>
							</Box>
						</TabPanel>

						<TabPanel p='0'>
							<Textarea
								variant='main'
								minH='180px'
								bg={dropBg}
								borderRadius='16px'
								placeholder={'Cole os CPFs aqui (Ctrl+V)\n\n123.456.789-09\n98765432100\n...'}
								value={pastedText}
								onChange={e => setPastedText(e.target.value)}
							/>
							<Flex justify='space-between' align='center' mt='8px'>
								<Text color={subColor} fontSize='xs'>
									Um por linha, ou separados por vírgula/ponto-e-vírgula. Com ou sem máscara.
								</Text>
								<Badge
									colorScheme={pastedCpfs.length > 0 ? 'green' : 'gray'}
									borderRadius='8px'
									px='10px'>
									{pastedCpfs.length} CPF{pastedCpfs.length === 1 ? '' : 's'}
								</Badge>
							</Flex>
						</TabPanel>
					</TabPanels>
				</Tabs>

				<Flex gap='12px' mt='16px' direction={{ base: 'column', sm: 'row' }}>
					<Input
						variant='main'
						placeholder='Nome do lote'
						value={name}
						onChange={e => setName(e.target.value)}
					/>
					<Button variant='brand' px='40px' onClick={handleUpload} isLoading={uploading}>
						Enviar
					</Button>
				</Flex>
			</Card>

			<Card>
				<Text fontWeight='800' fontSize='lg' color={textColor} mb='16px'>
					Meus lotes
				</Text>
				{loading && list.length === 0 && <Spinner color='brand.500' />}
				{!loading && list.length === 0 && (
					<Text color={subColor} fontSize='sm'>
						Nenhum lote enviado ainda.
					</Text>
				)}
				{list.map(batch => {
					const meta = STATUS_META[batch.status];
					const pct = batch.total > 0 ? Math.round((batch.processed / batch.total) * 100) : 0;
					return (
						<Flex
							key={batch.id}
							direction={{ base: 'column', md: 'row' }}
							justify='space-between'
							align={{ base: 'flex-start', md: 'center' }}
							gap='12px'
							py='16px'
							borderBottom='1px solid'
							borderColor='secondaryGray.200'>
							<Flex align='center' gap='12px' flex='1'>
								<Icon as={MdInsertDriveFile} w='28px' h='28px' color='brand.500' />
								<Box>
									<Text fontWeight='700' color={textColor}>
										{batch.name}
									</Text>
									<Text fontSize='xs' color={subColor}>
										{batch.createdAt ? format(new Date(batch.createdAt), 'dd/MM/yyyy HH:mm') : ''} ·{' '}
										{batch.processed}/{batch.total} · {batch.success} ok · {batch.fail} falhas
									</Text>
								</Box>
							</Flex>
							<Box w={{ base: '100%', md: '200px' }}>
								<Progress value={pct} borderRadius='8px' colorScheme='blue' h='8px' mb='6px' />
								<Flex justify='space-between'>
									<Badge colorScheme={meta.scheme} borderRadius='8px'>
										{meta.label}
									</Badge>
									<Text fontSize='xs' color={subColor}>
										{pct}%
									</Text>
								</Flex>
							</Box>
							<Button
								variant='lightBrand'
								size='sm'
								leftIcon={<Icon as={MdDownload} />}
								isDisabled={batch.status !== BatchStatus.COMPLETED}
								onClick={() => handleDownload(batch.id)}>
								Baixar
							</Button>
						</Flex>
					);
				})}
			</Card>
		</Box>
	);
}
